import { ToastrService } from 'ngx-toastr'
import { Observable, throwError, BehaviorSubject } from 'rxjs'
import { map, catchError, tap, switchMap, take } from 'rxjs/operators'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'

import { Vehicle } from './vehicle.object'

import { AppConfig } from '../../app.config'
import { Store } from '@ngrx/store'
import { AppState, getFlightNetworkCenters } from 'src/app/@store'
import { ErrorHandlerService } from 'src/app/common/error-handler.service'
import { PartInstance, PartInstanceResponsePayload } from './part_instance.object'
import { DEFAULT_FLOAT_DECIMALS } from '../../common/round-float.pipe'
import { VehicleMaintenanceDetails } from './vehicle-maintenance-details.object'
import { VehicleId } from '../../common/jrpc.service'

export interface Module {
  last_update_timestamp: string
  module: string
  version: string
}

interface AllowablePackageWeightRange {
  min_kg: number
  max_kg: number
}

enum RoundingType {
  Floor = 'floor',
  Ceil = 'ceil',
}

@Injectable()
export class VehiclesService {
  vehicleStatusWasUpdated$ = new BehaviorSubject<null>(null)

  constructor(
    private http: HttpClient,
    private appConfig: AppConfig,
    private toastr: ToastrService,
    private store: Store<AppState>,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  getAllVehiclesInCenter(): Observable<Vehicle[]> {
    const url = this.appConfig.config.server_api.vehicles.get

    return this.http.get(url).pipe(
      map((response: Partial<Vehicle>[]) => response.map((vehicle) => new Vehicle(vehicle))),
      switchMap((vehicles) =>
        this.store.select(getFlightNetworkCenters).pipe(
          take(1),
          map((centers) => {
            return vehicles.map((vehicle) => {
              const foundCenter = centers.find((center) => center?.id === vehicle.center_id)
              vehicle.center_name = foundCenter?.name
              return vehicle
            })
          }),
        ),
      ),
      map((vehicles: Vehicle[]) =>
        vehicles.sort((first, second) => {
          if (first.id > second.id) {
            return 1
          }
          if (first.id < second.id) {
            return -1
          }
          return 0
        }),
      ),
    )
  }

  getVehicle = (vehicleId: VehicleId): Observable<Vehicle> => {
    const url = `${this.appConfig.config.server_api.vehicles.get}${vehicleId}/`
    return this.http.get(url).pipe(map((response) => new Vehicle(response)))
  }

  getVehicleParts = (vehicleId: VehicleId): Observable<PartInstance[]> => {
    const url = `${this.appConfig.config.server_api.vehicles.get}${vehicleId}/parts/`

    return this.http.post<PartInstanceResponsePayload[] | null>(url, {}).pipe(
      map((parts) =>
        (parts ?? [])
          .sort((a, b) => {
            const [first, second] =
              a.part_number.number === b.part_number.number
                ? [a.serial, b.serial]
                : [a.part_number.number, b.part_number.number]
            return this.compare(first, second)
          })
          .map((partInstance) => new PartInstance(partInstance)),
      ),
    )
  }

  addVehicle(vehicle: Vehicle) {
    const url = this.appConfig.config.server_api.vehicles.add

    const params = {
      id: vehicle.id,
      name: vehicle.name,
      registration_number: vehicle.registration_number,
      center_id: vehicle.center_id,
      checklists: vehicle.checklists.map((checklist) => ({ checklist })),
    }

    return this.http.post(url, params).pipe(
      tap(() => {
        const message = `Vehicle with id: ${vehicle.id} successfully added`
        this.toastr.success(message, null, null)
      }),
      catchError((error) => {
        const message = `Can't add vehicle ${vehicle.id}`
        this.errorHandlerService.showErrorMessage(error, message)
        return throwError(error)
      }),
    )
  }

  editVehicle(vehicle: Vehicle) {
    const url = `${this.appConfig.config.server_api.vehicles.edit}${vehicle.id}/`
    const params = {
      id: vehicle.id,
      name: vehicle.name,
      registration_number: vehicle.registration_number,
      center_id: vehicle.center_id,
      is_in_service: vehicle.is_in_service,
      checklists: vehicle.checklists.map((checklist) => ({ checklist })),
    }

    return this.http.patch(url, params).pipe(
      tap((response) => {
        const message = `Vehicle successfully edited`
        this.toastr.success(message)
        return response
      }),
      catchError((error) => {
        const message = `Can't edit vehicle ${vehicle.id}`
        this.errorHandlerService.showErrorMessage(error, message)
        return throwError(error)
      }),
    )
  }

  getVehicleModuleVersions(vehicleId: VehicleId): Observable<Module[]> {
    const url = `${this.appConfig.config.server_api.vehicles.module_versions}${vehicleId}/module_versions/`

    return this.http.post<Module[]>(url, {}).pipe(
      catchError((error) => {
        const message = `Can't get module versions for vehicle ${vehicleId}`
        this.errorHandlerService.showErrorMessage(error, message)
        return throwError(error)
      }),
    )
  }

  getAllowablePackageWeightRange(): Observable<AllowablePackageWeightRange> {
    const url = this.appConfig.config.server_api.vehicles.allowable_package_weight_range

    return this.http.post<AllowablePackageWeightRange>(url, {}).pipe(
      map((response) => ({
        min_kg: this.round(response.min_kg, RoundingType.Ceil),
        max_kg: this.round(response.max_kg, RoundingType.Floor),
      })),
      catchError((error) => {
        const message = `Can't get allowable package weight range`
        this.errorHandlerService.showErrorMessage(error, message)
        return throwError(error)
      }),
    )
  }

  private round(value: number, roundingType: RoundingType): number {
    const factor = 10 ** DEFAULT_FLOAT_DECIMALS

    if (roundingType === RoundingType.Floor) {
      return Math.floor(value * factor) / factor
    }
    return Math.ceil(value * factor) / factor
  }

  private compare(first: string, second: string): number {
    const firstNumber = Number(first)
    const secondNumber = Number(second)
    const areBothNumbers = ![firstNumber, secondNumber].includes(NaN)

    if (areBothNumbers) {
      return firstNumber - secondNumber
    }
    if (first > second) {
      return 1
    }
    if (first < second) {
      return -1
    }
    return 0
  }

  get vehiclesMaintenanceDetails$(): Observable<VehicleMaintenanceDetails[]> {
    const url = this.appConfig.config.server_api.vehicles.vehicles_maintenance
    return this.http
      .get<VehicleMaintenanceDetails[]>(url)
      .pipe(map((vehicles) => vehicles.map((details) => new VehicleMaintenanceDetails(details))))
  }
}
